import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../config/constants";
import { getSessionsWithSomeHitOrError } from "../functions";
// Components
import { ScreenshotContainer } from "../sections/generalStyles";
import Loader from "../../loader";
import UseFetch from "../../../config/useFetch";
import PatientNavigation from "../../patientNavigation";
import MenuNavigation from "../sections/menuNavigation";
import SessionNavigation from "./sections/sessionNavigation";
import { GetProtocolData } from "../sections/getProtocolData";
import Title from "../../title";
import DownloadReportPDF from "../sections/downloadReportPDF";
import SessionsHeader from "./sections/sessionsHeader";
import ProtocolSession from "./sections/protocolSession";
import EvaluationsData from "./sections/evaluationsData";

const SessionDetails = () => {
  const [sessionDates, setSessionDates] = useState([]);
  const [currentSessionDate, setCurrentSessionDate] = useState("");
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const { patientId, sessionNumber } = useParams();
  const [t] = useTranslation("global");
  const [title, setTitle] = useState("");

  // Set plot images
  const sessionsListRef = useRef(null);
  const evaluationsRef = useRef(null);

  // Fetching session dates
  useEffect(() => {
    UseFetch("get", `${WEB_SERVICE_MIMS}/icu/get-sessions/${patientId}`)
      .then(({ data }) => {
        const sessionsWithSomeHitOrError = getSessionsWithSomeHitOrError(data);

        const sessionDatesArray = sessionsWithSomeHitOrError.reduce((acc, session) => {
          const date = session.STARTING_DATE.split("T")[0];
          if (!acc.includes(date)) acc.push(date);
          return acc;
        }, []);

        const sessionDatesSorted = Object.values(sessionDatesArray).sort((a, b) => new Date(a) - new Date(b));
        const currentSessionDate = sessionDatesSorted[sessionNumber - 1];

        const sessionsFilteredByDate = sessionsWithSomeHitOrError.filter((session) => {
          return moment(session.STARTING_DATE).startOf("day").isSame(moment(currentSessionDate, "YYYY-MM-DD").startOf("day"));
        });

        setSessions(sessionsFilteredByDate);
        setSessionDates(sessionDatesSorted);
        setCurrentSessionDate(currentSessionDate);
        setIsLoadingSessions(false);
      })
      .catch((error) => {
        console.log("Get sessions: ", error);
      });

    return () => {
      setSessions([]);
      setSessionDates([]);
      setCurrentSessionDate("");
      setIsLoadingSessions(true);
    };
  }, [patientId, sessionNumber]);

  // Set title
  useEffect(() => {
    setTitle(`${t("session").toUpperCase()} ${sessionNumber} - ${moment(currentSessionDate).format("DD/MM/YYYY")}`);

    return () => {
      setTitle("");
    };
  }, [t, sessionNumber, currentSessionDate]);

  const renderSessionsList = () => {
    return sessions.map((session) => {
      const sessionId = session.SESSION_ID;
      const protocolId = session.PROTOCOL_ID;
      const { img, name } = GetProtocolData(protocolId);

      return <ProtocolSession key={sessionId} session={session} protocolImg={img} protocolName={t(`protocols_${name}`)} />;
    });
  };

  return (
    <>
      {isLoadingSessions ? (
        <Loader />
      ) : (
        <>
          <PatientNavigation patientId={patientId} section={"icu"} />
          <MenuNavigation />
          <SessionNavigation numberOfSessions={sessionDates.length} />
          <Title title={title} marginBottom="10px" />
          <DownloadReportPDF
            view={title}
            plotsRef={{
              sessionsListRef,
              evaluationsRef,
            }}
          />
          <ScreenshotContainer ref={sessionsListRef}>
            <SessionsHeader />
            {renderSessionsList()}
          </ScreenshotContainer>
          <ScreenshotContainer ref={evaluationsRef}>
            <EvaluationsData currentSessionDate={currentSessionDate} />
          </ScreenshotContainer>
        </>
      )}
    </>
  );
};

export default SessionDetails;
